/**
 * Centralized ELO tier definitions for bracket-colored display across all surfaces.
 *
 * Tiers scale to 10,000+ without requiring component changes — just extend ELO_TIERS.
 *
 * Color progression: charcoal → blue → indigo → violet → crimson/red → gold.
 * Red and gold are reserved for the top two brackets to signal elite skill ceiling.
 */

export const ELO_TIERS = [
  {
    id: 'unranked',
    label: 'Unranked',
    shortLabel: 'NR',
    min: 0,
    max: 999,
    // Charcoal — placement, no rank yet
    bg: 'rgba(72, 78, 86, 0.85)',
    border: 'rgba(130, 140, 155, 0.5)',
    text: 'rgba(200, 208, 218, 0.9)',
  },
  {
    id: 'beginner',
    label: 'Beginner',
    shortLabel: 'B',
    min: 1000,
    max: 1499,
    // Steel blue
    bg: 'rgba(38, 97, 156, 0.85)',
    border: 'rgba(82, 152, 220, 0.6)',
    text: 'rgba(210, 232, 255, 0.92)',
  },
  {
    id: 'rising',
    label: 'Rising',
    shortLabel: 'R',
    min: 1500,
    max: 1999,
    // Medium blue
    bg: 'rgba(28, 76, 152, 0.88)',
    border: 'rgba(68, 132, 228, 0.65)',
    text: 'rgba(195, 220, 255, 0.93)',
  },
  {
    id: 'advanced',
    label: 'Advanced',
    shortLabel: 'A',
    min: 2000,
    max: 2499,
    // Indigo-blue
    bg: 'rgba(56, 52, 168, 0.88)',
    border: 'rgba(110, 104, 232, 0.65)',
    text: 'rgba(210, 208, 255, 0.93)',
  },
  {
    id: 'elite',
    label: 'Elite',
    shortLabel: 'E',
    min: 2500,
    max: 3499,
    // Deep indigo-violet
    bg: 'rgba(78, 38, 170, 0.88)',
    border: 'rgba(140, 90, 230, 0.65)',
    text: 'rgba(220, 205, 255, 0.93)',
  },
  {
    id: 'master',
    label: 'Master',
    shortLabel: 'M',
    min: 3500,
    max: 4999,
    // Purple-violet
    bg: 'rgba(100, 30, 160, 0.88)',
    border: 'rgba(168, 80, 228, 0.65)',
    text: 'rgba(230, 205, 255, 0.93)',
  },
  {
    id: 'champion',
    label: 'Champion',
    shortLabel: 'C',
    min: 5000,
    max: 6999,
    // Deep violet-magenta
    bg: 'rgba(118, 20, 140, 0.88)',
    border: 'rgba(195, 60, 215, 0.65)',
    text: 'rgba(245, 205, 255, 0.93)',
  },
  {
    id: 'apex',
    label: 'Apex',
    shortLabel: 'AP',
    min: 7000,
    max: 8499,
    // Crimson — signals extreme skill, one tier below top
    bg: 'rgba(164, 20, 40, 0.90)',
    border: 'rgba(232, 58, 80, 0.70)',
    text: 'rgba(255, 205, 210, 0.94)',
  },
  {
    id: 'legendary',
    label: 'Legendary',
    shortLabel: 'LG',
    min: 8500,
    max: Infinity,
    // Gold — reserved for the true top tier
    bg: 'rgba(148, 108, 12, 0.92)',
    border: 'rgba(220, 168, 28, 0.75)',
    text: 'rgba(255, 238, 180, 0.96)',
  },
];

/** The fallback tier shown when value is null / not a number. */
export const FALLBACK_TIER = ELO_TIERS[0];

/**
 * Returns the tier object for a given numeric ELO value.
 * Returns FALLBACK_TIER when value is null, undefined, or NaN.
 */
export function getEloTier(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return FALLBACK_TIER;
  return ELO_TIERS.find((t) => num >= t.min && num <= t.max) || FALLBACK_TIER;
}

/**
 * Progress through the current tier toward the next one.
 */
export function getEloTierProgress(value) {
  const tier = getEloTier(value);
  const idx = ELO_TIERS.findIndex((t) => t.id === tier.id);
  const nextTier = idx >= 0 ? ELO_TIERS[idx + 1] : null;
  const num = Number(value);
  if (!nextTier || !Number.isFinite(num)) {
    return { progress: 1, eloToNext: 0, isMax: true, nextTier: null };
  }
  const span = Math.max(1, nextTier.min - tier.min);
  return {
    progress: Math.min(1, Math.max(0, (num - tier.min) / span)),
    eloToNext: Math.max(0, Math.round(nextTier.min - num)),
    isMax: false,
    nextTier,
  };
}

/**
 * Formats an ELO value for display.
 * - Null/non-finite → '-'
 * - Integer values → plain string
 * - Float → rounded integer string
 */
export function formatElo(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return String(Math.round(num));
}
